import { useState, useRef, useCallback, ReactNode } from 'react'
import { useUnit } from '@/modules/units/contexts/UnitContext'
import { useAuth } from '@/shared/contexts/AuthContext'
import { useTranslation } from '@/shared/contexts/TranslationContext'
import { usePermissions } from '@/modules/units/usePermissions'
import {
  type FilterField,
  type DataTableColumn,
} from '@/shared/components/entities'
import { EntityListPage, type CardConfig } from '@/shared/components/entities/EntityListPage'
import { Modal } from '@/shared/components/ui'
import {
  createParty,
  updateParty,
  deleteOrInactivateParties,
  listPartiesWithStats,
  reactivateParty,
  mergeParty,
} from '@/modules/registrations/contacts/contactsService'
import type { Party, PartyWithStats } from '@/modules/registrations/contacts/contactsTypes'
import { openWhatsAppWindow } from '@/lib/integrations/whatsapp'
import { matchesSearch } from '@/lib/search'
import { PartyDocumentModal } from '@/modules/registrations/contacts/PartyDocumentModal'
import type { Role } from '@/lib/permissions/types'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PartyFormInput {
  name:     string
  whatsapp: string
  pix_key:  string
  notes:    string
}

/**
 * Runtime context passed to every extension callback.
 * Extensions receive this so they can access live state without coupling to internals.
 */
export interface ContactsPageContext {
  unitId:        string
  unitName:      string
  currentLocale: string
  /** Translate a key relative to the active namespace, e.g. tc('actions.edit') */
  tc:            (key: string, params?: Record<string, string>) => string
  reloadKey:     number
  setReloadKey:  React.Dispatch<React.SetStateAction<number>>
  /** Current base type filter ('all' | 'inactive') */
  typeFilter:    'all' | 'inactive'
  /** Live ref to the loaded parties array */
  parties:       React.MutableRefObject<PartyWithStats[]>
}

/**
 * Extension points — inject context-specific behaviour without touching the base.
 * Think of it as "class inheritance via props".
 */
export interface ContactsPageExtensions {
  /**
   * Replaces or augments the leading chips/controls row.
   * Receives the base <select> node as `baseChips` so you can wrap it.
   */
  chipsLeading?: (ctx: ContactsPageContext, baseChips: ReactNode) => ReactNode

  /**
   * Override applyFilters.  Call `base(...)` to keep the core active/inactive logic
   * and add your own predicates on top.
   */
  applyFilters?: (
    parties:      PartyWithStats[],
    query:        string,
    filterValues: Record<string, any> | undefined,
    base:         (parties: PartyWithStats[], query: string, filterValues?: Record<string, any>) => PartyWithStats[],
  ) => PartyWithStats[]

  /** Extra table columns appended after the base (name / whatsapp / type) columns */
  extraTableColumns?: (ctx: ContactsPageContext) => DataTableColumn<PartyWithStats>[]

  /** Card badge — omit to show no badge */
  getCardBadge?: (party: PartyWithStats, ctx: ContactsPageContext) => { label: string; variant: 'success' | 'warning' | 'danger' } | null

  /**
   * Extra card/grid actions prepended before the base merge/docs actions.
   * Return an empty array to add nothing.
   */
  extraActions?: (party: PartyWithStats, ctx: ContactsPageContext) => Array<{
    label:    string
    icon?:    ReactNode
    onClick:  () => void
    variant?: 'success' | 'warning' | 'danger' | 'info'
  }>

  /**
   * Extra row-dropdown actions appended after the base ones.
   */
  extraRowActions?: (party: PartyWithStats, ctx: ContactsPageContext) => Array<{
    label:   string
    icon?:   ReactNode
    onClick: () => void
  }>

  /** Row quick-action button; return null to show none */
  rowQuickAction?: (party: PartyWithStats, ctx: ContactsPageContext) => {
    icon:    ReactNode
    label:   string
    onClick: () => void
  } | null

  /** Extra modals rendered after the base page (position: after MergePartyModal) */
  extraModals?: (ctx: ContactsPageContext) => ReactNode

  /** Override selectable predicate */
  isEntitySelectable?: (party: PartyWithStats, ctx: ContactsPageContext) => boolean
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function formatCurrency(v: number, locale?: string) {
  const loc = locale || document.documentElement.lang || 'pt-BR'
  let currency = 'BRL'
  if (loc.startsWith('en')) currency = 'USD'
  else if (loc.startsWith('es')) currency = 'EUR'
  return v.toLocaleString(loc, { style: 'currency', currency })
}

export function buildWhatsAppUrl(phone: string, message: string): string {
  const clean  = phone.replace(/\D/g, '')
  const number = clean.startsWith('55') ? clean : `55${clean}`
  return `https://wa.me/${number}?text=${encodeURIComponent(message)}&app_absent=0`
}

const TYPE_STYLES: Record<string, string> = {
  customer: 'bg-blue-100   text-blue-700   dark:bg-blue-900/30   dark:text-blue-400',
  supplier: 'bg-slate-100  text-slate-600  dark:bg-slate-700     dark:text-slate-300',
  both:     'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400',
}

export const ICON_BG_STYLES: Record<string, string> = {
  customer: 'p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30',
  supplier: 'p-2 rounded-lg bg-slate-100 dark:bg-slate-700',
  both:     'p-2 rounded-lg bg-violet-100 dark:bg-violet-900/30',
}

export function TypeChip({
  type,
  labels,
}: {
  type:   string | null
  labels: Record<string, string>
}) {
  if (!type) return <span className="text-slate-400 text-xs">—</span>
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${TYPE_STYLES[type] ?? ''}`}>
      {labels[type] ?? type}
    </span>
  )
}

export function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
      <path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.975-1.395A9.959 9.959 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18a7.948 7.948 0 01-4.048-1.107l-.29-.172-2.953.828.815-2.876-.188-.296A8 8 0 1112 20z" />
    </svg>
  )
}

export function emptyStats() {
  return {
    score_pct: null, first_paid_at: null, last_paid_at: null,
    overdue_count: 0, overdue_total: 0,
    overdue_receivable_count: 0, overdue_receivable_total: 0,
    overdue_payable_count: 0, overdue_payable_total: 0,
    receivable_count: 0, receivable_total: 0,
    payable_count: 0, payable_total: 0,
    due_in_7_days_count: 0, due_in_7_days_total: 0,
    due_in_7_days_receivable_count: 0, due_in_7_days_receivable_total: 0,
    due_in_7_days_payable_count: 0, due_in_7_days_payable_total: 0,
    open_count: 0, open_total: 0,
  }
}

// ─── Merge Party Modal ────────────────────────────────────────────────────────

function MergePartyModal({
  source, allParties, onConfirm, onClose,
}: {
  source:     PartyWithStats
  allParties: PartyWithStats[]
  onConfirm:  (target: Party) => Promise<void>
  onClose:    () => void
}) {
  const [query,      setQuery]      = useState('')
  const [selected,   setSelected]   = useState<PartyWithStats | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error,      setError]      = useState<string | null>(null)

  const filtered = allParties
    .filter(p => p.id !== source.id && p.is_active)
    .filter(p => !query.trim() || matchesSearch(p.name, query))
    .slice(0, 8)

  const handleConfirm = async () => {
    if (!selected) return
    setSubmitting(true)
    setError(null)
    try { await onConfirm(selected) }
    catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao juntar contatos')
      setSubmitting(false)
    }
  }

  return (
    <Modal isOpen onClose={onClose} title={`Juntar "${source.name}" com...`} size="sm">
      <div className="space-y-4">
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Todas as contas de <strong>{source.name}</strong> serão transferidas para o contato escolhido e o contato antigo será excluído.
        </p>
        <input
          type="text"
          value={query}
          onChange={e => { setQuery(e.target.value); setSelected(null) }}
          placeholder="Buscar contato..."
          autoFocus
          className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
        />
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-700">
          {filtered.length === 0
            ? <p className="text-sm text-slate-400 text-center py-6">Nenhum contato encontrado</p>
            : filtered.map(p => (
              <button
                key={p.id} type="button" onClick={() => setSelected(p)}
                className={`w-full text-left px-3 py-2.5 text-sm transition-colors ${
                  selected?.id === p.id
                    ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 font-medium'
                    : 'hover:bg-slate-50 dark:hover:bg-slate-700/50 text-slate-800 dark:text-slate-200'
                }`}
              >
                {p.name}
                {p.whatsapp && <span className="ml-2 text-xs text-slate-400">{p.whatsapp}</span>}
              </button>
            ))
          }
        </div>
        {selected && (
          <div className="text-xs bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg px-3 py-2 text-amber-700 dark:text-amber-300">
            ⚠️ <strong>{source.name}</strong> será excluído e suas contas movidas para <strong>{selected.name}</strong>. Esta ação não pode ser desfeita.
          </div>
        )}
        {error && <p className="text-sm text-rose-600">{error}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} disabled={submitting}
            className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
            Cancelar
          </button>
          <button type="button" onClick={handleConfirm} disabled={!selected || submitting}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            {submitting ? 'Juntando...' : 'Juntar'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

interface ContactsPageProps {
  /** i18n namespace prefix, e.g. "registrations.contacts" or "billing.contacts" */
  namespace?:  string
  extensions?: ContactsPageExtensions
}

export function ContactsPage({
  namespace  = 'registrations.contacts',
  extensions,
}: ContactsPageProps) {
  const { t, locale }             = useTranslation()
  const { currentUnit, userRole } = useUnit()
  const permissions               = usePermissions(userRole)
  const { user: authUser }        = useAuth()

  const contactsRef = useRef<PartyWithStats[]>([])

  const [typeFilter,    setTypeFilter]    = useState<'all' | 'inactive'>('all')
  const [reloadKey,     setReloadKey]     = useState(0)
  const [mergeSource,   setMergeSource]   = useState<PartyWithStats | null>(null)
  const [documentParty, setDocumentParty] = useState<PartyWithStats | null>(null)

  const currentLocale = locale
  const unitId        = currentUnit?.id   ?? ''
  const unitName      = currentUnit?.name ?? ''

  const tc = useCallback(
    (key: string, params?: Record<string, string>) => t(`${namespace}.${key}`, params),
    [t, namespace],
  )

  // ── Context passed to every extension callback ────────────────────────────

  const ctx: ContactsPageContext = {
    unitId, unitName, currentLocale, tc,
    reloadKey, setReloadKey,
    typeFilter,
    parties: contactsRef,
  }

  // ── Label objects ─────────────────────────────────────────────────────────

  const typeLabels: Record<string, string> = {
    label:    'Tipo',
    customer: tc('contactType.customer'),
    supplier: tc('contactType.supplier'),
    both:     tc('contactType.both'),
    none:     tc('contactType.none'),
  }

  // ── Reactivate ────────────────────────────────────────────────────────────

  const handleReactivate = async (id: string) => {
    try {
      await reactivateParty(id)
      setReloadKey(k => k + 1)
    } catch (err) {
      console.error('[ContactsPage] reactivate error:', err)
      alert(tc('messages.error') || 'Erro ao reativar contato')
    }
  }

  // ── Base applyFilters ─────────────────────────────────────────────────────

  const baseApplyFilters = useCallback((
    parties:       PartyWithStats[],
    query:         string,
    _filterValues?: Record<string, any>,
  ): PartyWithStats[] => {
    let results = parties

    if (query && query.trim()) {
      results = results.filter(p =>
        matchesSearch(p.name, query) ||
        matchesSearch(p.whatsapp ?? '', query) ||
        matchesSearch(p.pix_key  ?? '', query) ||
        matchesSearch(p.notes    ?? '', query),
      )
    }

    if (typeFilter === 'inactive') results = results.filter(p => !p.is_active)
    else                           results = results.filter(p => p.is_active)

    return results
  }, [typeFilter])

  // ── EntityListPage config ─────────────────────────────────────────────────

  const config = {
    entityType:   `contacts-${unitId}`,
    filterFields: [] as FilterField[],

    isEntitySelectable: (party: PartyWithStats) =>
      extensions?.isEntitySelectable
        ? extensions.isEntitySelectable(party, ctx)
        : party.is_active || typeFilter === 'inactive',

    loadData: async () => {
      const data = await listPartiesWithStats(unitId, true)
      contactsRef.current = data
      return data
    },

    createEntity: async (input: PartyFormInput) => {
      const created = await createParty(unitId, input, authUser!.id)
      const parties = await listPartiesWithStats(unitId)
      const found   = parties.find(p => p.id === created.id)
      return found ?? ({ ...created, ...emptyStats() } as PartyWithStats)
    },

    updateEntity: async (id: string, input: PartyFormInput) => {
      const updated = await updateParty(id, input)
      const parties = await listPartiesWithStats(unitId)
      const found   = parties.find(p => p.id === updated.id)
      return found ?? ({ ...updated, ...emptyStats() } as PartyWithStats)
    },

    deleteEntity: async (id: string) => {
      await deleteOrInactivateParties(unitId, [id])
    },

    applyFilters: (parties: PartyWithStats[], query: string, filterValues?: Record<string, any>) =>
      extensions?.applyFilters
        ? extensions.applyFilters(parties, query, filterValues, baseApplyFilters)
        : baseApplyFilters(parties, query, filterValues),

    validateForm: (data: PartyFormInput) => {
      const errors: Record<string, string> = {}
      if (!data.name.trim()) errors.name = tc('messages.nameRequired')
      return errors
    },
  }

  // ── Table columns ─────────────────────────────────────────────────────────

  const baseColumns: DataTableColumn<PartyWithStats>[] = [
    {
      key:   'name',
      label: tc('list.columns.name'),
      render: (p) => <div className="font-medium text-slate-900 dark:text-white">{p.name}</div>,
    },
    {
      key:   'whatsapp',
      label: tc('list.columns.whatsapp'),
      render: (p) =>
        p.whatsapp ? (
          <a
            href={buildWhatsAppUrl(p.whatsapp, 'oi')}
            className="text-sm text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300 flex items-center gap-1"
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); openWhatsAppWindow(buildWhatsAppUrl(p.whatsapp!, 'oi')) }}
          >
            <WhatsAppIcon className="h-4 w-4 shrink-0" />
            {p.whatsapp}
          </a>
        ) : (
          <span className="text-slate-400 text-sm">—</span>
        ),
    },
    {
      key:   'party_type',
      label: tc('list.columns.type'),
      render: (p) => <TypeChip type={p.party_type} labels={typeLabels} />,
    },
  ]

  const tableColumns: DataTableColumn<PartyWithStats>[] = [
    ...baseColumns,
    ...(extensions?.extraTableColumns?.(ctx) ?? []),
  ]

  // ── Card config ───────────────────────────────────────────────────────────

  const cardConfig: CardConfig<PartyWithStats> = {
    getIcon: () => (
      <svg className="h-5 w-5 text-slate-700 dark:text-slate-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    ),
    getIconWrapperClass: (p) => ICON_BG_STYLES[p.party_type ?? 'customer'],
    getTitle:   (p) => p.name,
    getSubtitle: (p) => p.whatsapp ? (
      <a
        href={buildWhatsAppUrl(p.whatsapp, 'oi')}
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); openWhatsAppWindow(buildWhatsAppUrl(p.whatsapp!, 'oi')) }}
        className="text-xs text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300 flex items-center gap-1 font-mono"
      >
        <WhatsAppIcon className="h-3.5 w-3.5 shrink-0" />
        {p.whatsapp}
      </a>
    ) : undefined as any,
    getBadge: extensions?.getCardBadge
      ? (p) => extensions.getCardBadge!(p, ctx)
      : undefined,
    fields: [
      {
        key:      'party_type',
        label:    'Tipo',
        getValue: (p) => <TypeChip type={p.party_type} labels={typeLabels} />,
      },
    ],
    editLabel:   tc('actions.edit'),
    deleteLabel: tc('actions.delete'),
    getExtraActions: (p) => {
      const actions: any[] = []

      if (!p.is_active) {
        actions.push({
          label:   tc('actions.activate'),
          icon:    <CheckIcon />,
          onClick: () => handleReactivate(p.id),
          variant: 'success' as const,
        })
        extensions?.extraActions?.(p, ctx).forEach(a => actions.push(a))
        return actions
      }

      // Extension actions first (e.g. financial profile, pendencies, charge WA)
      extensions?.extraActions?.(p, ctx).forEach(a => actions.push(a))

      if (permissions.canEditEntries) {
        actions.push({
          label:   'Juntar com...',
          icon:    <MergeIcon />,
          onClick: () => setMergeSource(p),
        })
        actions.push({
          label:   tc('actions.documents'),
          icon:    <DocumentIcon />,
          onClick: () => setDocumentParty(p),
          variant: 'info' as const,
        })
      }

      return actions
    },
  }

  // ── Form renderer ─────────────────────────────────────────────────────────

  const renderForm = (
    formData:    PartyFormInput,
    setFormData: (d: PartyFormInput) => void,
    formErrors:  Record<string, string>,
    submitting:  boolean,
  ) => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          {tc('record.fields.name')} *
        </label>
        <input type="text" value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder={tc('record.fields.namePlaceholder')} disabled={submitting}
          className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:opacity-50"
        />
        {formErrors.name && <p className="mt-1 text-sm text-rose-600">{formErrors.name}</p>}
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          {tc('record.fields.whatsapp')}
        </label>
        <input type="tel" value={formData.whatsapp}
          onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
          placeholder={tc('record.fields.whatsappPlaceholder')} disabled={submitting}
          className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:opacity-50"
        />
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{tc('record.fields.whatsappHelp')}</p>
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          {tc('record.fields.pixKey')}
        </label>
        <input type="text" value={formData.pix_key}
          onChange={(e) => setFormData({ ...formData, pix_key: e.target.value })}
          placeholder={tc('record.fields.pixKeyPlaceholder')} disabled={submitting}
          className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400 font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:opacity-50"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          {tc('record.fields.notes')}
        </label>
        <textarea rows={3} value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          placeholder={tc('record.fields.notesPlaceholder')} disabled={submitting}
          className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:opacity-50 resize-none"
        />
      </div>
    </div>
  )

  // ── Page labels ───────────────────────────────────────────────────────────

  const labels = {
    title:             tc('list.title'),
    description:       tc('list.description'),
    newButton:         tc('actions.new'),
    searchPlaceholder: tc('filters.searchPlaceholder'),
    emptyTitle:        tc('list.empty.title'),
    emptyDescription:  tc('list.empty.description'),
    modalTitleNew:     tc('record.new'),
    modalTitleEdit:    tc('record.edit'),
    modalTitleView:    tc('record.view'),
    deleteTitle:       tc('record.delete'),
    deleteMessage:     tc('record.deleteConfirm'),
    deleteButton:      tc('actions.delete'),
    cancelButton:      tc('actions.cancel'),
    submitButton:      tc('actions.create'),
    updateButton:      tc('actions.update'),
    viewCard:          tc('filters.viewCard'),
    viewTable:         tc('filters.viewTable'),
    clearSearch:       tc('filters.clear'),
  }

  const pageIcon = (
    <svg className="h-6 w-6 text-slate-700 dark:text-slate-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  )

  // ── Chips leading ─────────────────────────────────────────────────────────

  const baseChips = (
    <select
      value={typeFilter}
      onChange={(e) => setTypeFilter(e.target.value as any)}
      className="text-sm rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-3 py-1 min-w-[180px] text-slate-900 dark:text-white"
    >
      <option value="all">{tc('filters.presets.all')}</option>
      <option value="inactive">{tc('filters.presets.contactsInactive')}</option>
    </select>
  )

  const chipsLeading = extensions?.chipsLeading
    ? extensions.chipsLeading(ctx, baseChips)
    : <div className="flex items-center gap-2">{baseChips}</div>

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <EntityListPage<PartyWithStats, PartyFormInput>
        key={reloadKey}
        config={config}
        cardCustomizationStorageKey={`contacts-${unitId}-cards-v2`}
        userRole={userRole}
        canDelete={(p) => !!p.is_active}
        labels={{
          ...labels,
          bulkDeleteButton: typeFilter === 'inactive' ? tc('list.inactiveSelected') : tc('list.deleteSelected'),
        }}
        icon={pageIcon}
        filterFields={[]}
        tableColumns={tableColumns}
        cardConfig={cardConfig}
        renderForm={renderForm}
        entityToFormData={(p) => ({
          name:     p.name,
          whatsapp: p.whatsapp ?? '',
          pix_key:  p.pix_key  ?? '',
          notes:    p.notes    ?? '',
        })}
        chipsLeading={chipsLeading}
        emptyFormData={() => ({ name: '', whatsapp: '', pix_key: '', notes: '' })}
        enableBulkSelection
        hideBulkDelete={typeFilter === 'inactive'}
        onBulkCustomAction={typeFilter === 'inactive' ? {
          label:   tc('actions.activate'),
          icon:    <CheckIcon />,
          handler: async (ids: string[]) => {
            for (const id of ids) await reactivateParty(id)
            setReloadKey(k => k + 1)
          },
        } : undefined}
        extraRowActions={(p: PartyWithStats) => {
          const actions: any[] = []
          if (!p.is_active) {
            actions.push({
              label:   tc('actions.activate'),
              icon:    <CheckIcon />,
              onClick: () => handleReactivate(p.id),
            })
          }
          if (p.is_active && permissions.canEditEntries) {
            actions.push({
              label:   'Juntar com...',
              icon:    <MergeIcon />,
              onClick: () => setMergeSource(p),
            })
          }
          extensions?.extraRowActions?.(p, ctx).forEach(a => actions.push(a))
          return actions
        }}
        rowQuickAction={extensions?.rowQuickAction
          ? (p: PartyWithStats) => extensions.rowQuickAction!(p, ctx)
          : undefined
        }
        namespace={namespace}
      />

      {mergeSource && (
        <MergePartyModal
          source={mergeSource}
          allParties={contactsRef.current}
          onConfirm={async (target) => {
            await mergeParty(mergeSource.id, target, userRole || undefined)
            setMergeSource(null)
            setReloadKey(k => k + 1)
          }}
          onClose={() => setMergeSource(null)}
        />
      )}

      {documentParty && (
        <PartyDocumentModalWrapper
          party={documentParty}
          userRole={userRole}
          onClose={() => setDocumentParty(null)}
          onUpdated={(updated: PartyWithStats) => {
            setDocumentParty(updated)
            setReloadKey(k => k + 1)
          }}
        />
      )}

      {extensions?.extraModals?.(ctx)}
    </>
  )
}

// ─── Inline icon components ───────────────────────────────────────────────────

function CheckIcon() {
  return (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  )
}

function MergeIcon() {
  return (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
    </svg>
  )
}

function DocumentIcon() {
  return (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  )
}

function PartyDocumentModalWrapper(props: {
  party:     PartyWithStats
  userRole:  string | null | undefined
  onClose:   () => void
  onUpdated: (p: PartyWithStats) => void
}) {
  return <PartyDocumentModal {...props} userRole={props.userRole as Role | null | undefined} />
}
